import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { SimplePieChart } from './SimplePieChart';
import { SimpleBarChart } from './SimpleChartComponents';
import { QuestionReportData } from '../../types';
import { RawDataSection } from '../RawDataSection';

interface ChartRendererProps {
  questionReport: QuestionReportData;
  size?: 'small' | 'medium' | 'large';
  onSectionHover?: (label: string, value: number, responseId?: string) => void;
  onSectionClick?: (label: string, value: number, responseId?: string) => void;
  className?: string;
  showTitle?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
}

interface ChartableQuestionTypes {
  YES_NO: boolean;
  SINGLE_SELECT: boolean;
  MULTI_SELECT: boolean;
  RANGE: boolean;
}

const CHARTABLE_TYPES: ChartableQuestionTypes = {
  YES_NO: true,
  SINGLE_SELECT: true,
  MULTI_SELECT: true,
  RANGE: true,
};

const getQuestionTitle = (questionReport: QuestionReportData): string => {
  // Try to get English or Norwegian title, fallback to first available
  return (
    questionReport.questionTitle?.en ||
    questionReport.questionTitle?.no ||
    Object.values(questionReport.questionTitle)[0] ||
    'Untitled Question'
  );
};

export const ChartRenderer: React.FC<ChartRendererProps> = ({
  questionReport,
  size = 'medium',
  onSectionHover: _onSectionHover,
  onSectionClick: _onSectionClick,
  className,
  showTitle = true,
  showLegend: _showLegend = true,
  showTooltip: _showTooltip = true,
}) => {
  const { questionType, chartConfig, rawDataSummary } = questionReport;

  // Check if this question type supports charts
  if (!CHARTABLE_TYPES[questionType as keyof ChartableQuestionTypes]) {
    // For non-chartable questions, show raw data summary
    return (
      <Box className={className}>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            {getQuestionTitle(questionReport)}
          </Typography>
        )}

        {rawDataSummary && (
          <Box>
            {/* Summary stats */}
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Total Responses:</strong> {rawDataSummary.totalAnswers}
              </Typography>
            </Box>

            {/* All responses in card layout */}
            {rawDataSummary.sampleAnswers && rawDataSummary.sampleAnswers.length > 0 && (
              <RawDataSection
                responses={rawDataSummary.sampleAnswers}
                maxResponses={undefined}
                compact={false}
              />
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Chart is available for this question type
  if (!chartConfig) {
    return (
      <Box className={className}>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            {getQuestionTitle(questionReport)}
          </Typography>
        )}

        <Alert severity="warning">
          <Typography variant="body2">Chart data is not available for this question.</Typography>
        </Alert>
      </Box>
    );
  }

  const { chartType, data } = chartConfig;

  // Get chart height based on size
  const getChartHeight = () => {
    switch (size) {
      case 'small':
        return 250;
      case 'large':
        return 450;
      default:
        return 350;
    }
  };

  // Determine which chart component to use
  const renderChart = () => {
    const yesNoColors = ['#388e3c', '#dc004e'];
    const getDisplayData = () => {
      if (questionType === 'YES_NO') {
        return {
          ...data,
          datasets: data.datasets.map((ds, i) =>
            i === 0
              ? { ...ds, backgroundColor: yesNoColors, borderColor: yesNoColors }
              : ds
          ),
        };
      }
      return data;
    };

    switch (chartType) {
      case 'PIE':
        return <SimplePieChart data={getDisplayData()} height={getChartHeight()} variant="pie" />;

      case 'DONUT':
        return <SimplePieChart data={getDisplayData()} height={getChartHeight()} variant="donut" />;

      case 'BAR':
      case 'LINE':
        return <SimpleBarChart data={getDisplayData()} orientation="vertical" height={getChartHeight()} />;

      case 'HORIZONTAL_BAR':
        return <SimpleBarChart data={getDisplayData()} orientation="horizontal" height={getChartHeight()} />;

      default:
        return (
          <Alert severity="error">
            <Typography variant="body2">Unsupported chart type: {chartType}</Typography>
          </Alert>
        );
    }
  };

  return (
    <Box className={className}>
      {/* Show participation stats if available */}
      {rawDataSummary && (
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Total Responses:</strong> {rawDataSummary.totalAnswers}
          </Typography>
        </Box>
      )}

      {renderChart()}
    </Box>
  );
};
